import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
  articlePublishedTime,
  articleModifiedTime,
}: SEOHeadProps) {
  useEffect(() => {
    const fullTitle = `${title} - 51cg网`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property?: string) => {
      let el: HTMLMetaElement | null;
      if (property) {
        el = document.querySelector(`meta[property="${property}"]`);
      } else {
        el = document.querySelector(`meta[name="${name}"]`);
      }
      if (el) {
        el.setAttribute('content', content);
      } else {
        const meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', fullTitle, 'og:title');
    setMeta('og:description', description, 'og:description');
    setMeta('og:type', ogType, 'og:type');
    setMeta('og:url', canonicalUrl || window.location.href, 'og:url');
    if (ogImage) setMeta('og:image', ogImage, 'og:image');

    setMeta('twitter:card', 'summary_large_image', 'twitter:card');
    setMeta('twitter:title', fullTitle, 'twitter:title');
    setMeta('twitter:description', description, 'twitter:description');
    if (ogImage) setMeta('twitter:image', ogImage, 'twitter:image');

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl && canonicalUrl) {
      linkEl.setAttribute('href', canonicalUrl);
    } else if (canonicalUrl) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      document.head.appendChild(link);
    }

    if (articlePublishedTime || articleModifiedTime) {
      const existingArticle = document.querySelector('script[type="application/ld+json"][data-article="true"]');
      if (existingArticle) existingArticle.remove();

      if (schema) {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-article', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }

    return () => {
      // cleanup happens on unmount
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schema, articlePublishedTime, articleModifiedTime]);

  return null;
}